import GlassCard from '@/components/GlassCard';
import React, { useState } from 'react';
import clsx from 'clsx';

interface CompletedClass {
    class_title: string;
    completed_at: string;
}

interface EvaluationAttempt {
    id: number;
    evaluation_title: string;
    score: number;
    total_score: number;
    percentage: number;
    passed: boolean;
    completed_at: string | null;
}

interface AssignmentSubmission {
    id: number;
    assignment_title: string;
    status: string;
    teacher_comment: string;
    reviewed_at: string | null;
    reviewer_name: string | null;
}

interface CourseEntry {
    course: { id: number; title: string };
    progress_percentage: number;
    total_classes: number;
    completed_classes_count: number;
    evaluation_attempts: EvaluationAttempt[];
    certificates: {
        id: number;
        image_url: string | null;
        issued_at: string | null;
        evaluation: { id: number; title: string } | null;
    }[];
    completed_classes: CompletedClass[];
    assignments: AssignmentSubmission[];
}

interface FichaCategory {
    category: string;
    courses: CourseEntry[];
}

interface FichaProps {
    ficha: FichaCategory[];
}

const COURSE_SUB_TABS = [
    { id: 'evaluations', label: 'Evaluaciones' },
    { id: 'certificates', label: 'Certificados' },
    { id: 'classes', label: 'Clases Completadas' },
    { id: 'assignments', label: 'Tareas' },
] as const;

const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const Ficha: React.FC<FichaProps> = ({ ficha }) => {
    const [courseSubTab, setCourseSubTab] = useState<Record<number, string>>({});

    const getCourseSubTab = (courseId: number) => courseSubTab[courseId] ?? 'evaluations';

    const handleCourseSubTabChange = (courseId: number, subtab: string) => {
        setCourseSubTab((prev) => ({ ...prev, [courseId]: subtab }));
    };

    if (ficha.length === 0) {
        return (
            <p className="text-center text-gray-500 dark:text-neutral-400">No hay inscripciones</p>
        );
    }

    return (
        <div className="space-y-8">
            {ficha.map((group) => (
                <div key={group.category}>
                    <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                        {group.category}
                    </h2>
                    <div className="space-y-6">
                        {group.courses.map((courseEntry) => {
                            const {
                                course,
                                progress_percentage,
                                total_classes,
                                completed_classes_count,
                                evaluation_attempts,
                                certificates: courseCerts,
                                completed_classes: courseClasses,
                            } = courseEntry;
                            const subTab = getCourseSubTab(course.id);

                            return (
                                <GlassCard
                                    key={course.id}
                                    className="overflow-hidden shadow-lg"
                                >
                                    <div className="p-4">
                                        <h3 className="mb-3 font-semibold text-gray-900 dark:text-white">
                                            {course.title}
                                        </h3>
                                        <div className="mb-1 flex justify-between text-sm">
                                            <span className="text-gray-600 dark:text-neutral-400">Progreso</span>
                                            <span className="font-medium text-gray-900 dark:text-white">
                                                {completed_classes_count} / {total_classes} clases ({progress_percentage}%)
                                            </span>
                                        </div>
                                        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-neutral-700">
                                            <div
                                                className="h-full bg-theme-primary"
                                                style={{ width: `${progress_percentage}%` }}
                                            />
                                        </div>
                                    </div>

                                    <div className="course-sub-tabs flex gap-2 border-t border-white/40 px-4 py-3 dark:border-white/10">
                                        {COURSE_SUB_TABS.map((st) => (
                                            <button
                                                key={st.id}
                                                type="button"
                                                onClick={() => handleCourseSubTabChange(course.id, st.id)}
                                                className={clsx(
                                                    'whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
                                                    subTab === st.id
                                                        ? 'bg-theme-primary text-theme-on-primary'
                                                        : 'course-sub-tab-inactive',
                                                )}
                                            >
                                                {st.label}
                                            </button>
                                        ))}
                                    </div>

                                    <div className="p-4">
                                        {subTab === 'evaluations' && (
                                            evaluation_attempts.length === 0 ? (
                                                <p className="text-sm text-gray-500 dark:text-neutral-400">Sin intentos de evaluación</p>
                                            ) : (
                                                <div className="overflow-x-auto">
                                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-700">
                                                        <thead className="bg-white/40 dark:bg-white/5">
                                                            <tr>
                                                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">Evaluación</th>
                                                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">Puntaje</th>
                                                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">Estado</th>
                                                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">Fecha</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-white/30 dark:divide-white/10">
                                                            {evaluation_attempts.map((attempt) => (
                                                                <tr key={attempt.id}>
                                                                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900 dark:text-white">{attempt.evaluation_title}</td>
                                                                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                                        {attempt.score}/{attempt.total_score} ({attempt.percentage.toFixed(1)}%)
                                                                    </td>
                                                                    <td className="whitespace-nowrap px-4 py-3">
                                                                        {attempt.passed ? (
                                                                            <span className="inline-flex rounded-full bg-green-100 px-2 text-xs font-semibold leading-5 text-green-800 dark:bg-green-900/30 dark:text-green-400">Aprobado</span>
                                                                        ) : (
                                                                            <span className="inline-flex rounded-full bg-red-100 px-2 text-xs font-semibold leading-5 text-red-800 dark:bg-red-900/30 dark:text-red-400">Reprobado</span>
                                                                        )}
                                                                    </td>
                                                                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500 dark:text-neutral-400">{formatDateTime(attempt.completed_at)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )
                                        )}

                                        {subTab === 'certificates' && (
                                            courseCerts.length === 0 ? (
                                                <p className="text-sm text-gray-500 dark:text-neutral-400">Sin certificados</p>
                                            ) : (
                                                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                                    {courseCerts.map((cert) => (
                                                        <GlassCard key={cert.id} className="p-4 shadow">
                                                            {cert.image_url && (
                                                                <img src={cert.image_url} alt={cert.evaluation?.title} className="mb-3 w-full rounded" />
                                                            )}
                                                            <h4 className="font-semibold text-gray-900 dark:text-white">{cert.evaluation?.title}</h4>
                                                            <p className="mt-2 text-xs text-gray-400 dark:text-neutral-500">
                                                                Emitido: {cert.issued_at ? formatDate(cert.issued_at) : 'N/A'}
                                                            </p>
                                                        </GlassCard>
                                                    ))}
                                                </div>
                                            )
                                        )}

                                        {subTab === 'classes' && (
                                            courseClasses.length === 0 ? (
                                                <p className="text-sm text-gray-500 dark:text-neutral-400">Sin clases completadas</p>
                                            ) : (
                                                <div className="overflow-x-auto">
                                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-700">
                                                        <thead className="bg-white/40 dark:bg-white/5">
                                                            <tr>
                                                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">Clase</th>
                                                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">Fecha</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-white/30 dark:divide-white/10">
                                                            {courseClasses.map((cc, idx) => (
                                                                <tr key={idx}>
                                                                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900 dark:text-white">{cc.class_title}</td>
                                                                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500 dark:text-neutral-400">{formatDateTime(cc.completed_at)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )
                                        )}

                                        {subTab === 'assignments' && (
                                            courseEntry.assignments.length === 0 ? (
                                                <p className="text-sm text-gray-500 dark:text-neutral-400">Sin tareas entregadas</p>
                                            ) : (
                                                <div className="overflow-x-auto">
                                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-700">
                                                        <thead className="bg-white/40 dark:bg-white/5">
                                                            <tr>
                                                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">Tarea</th>
                                                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">Estado</th>
                                                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">Comentario</th>
                                                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">Revisado</th>
                                                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-neutral-400">Revisor</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-white/30 dark:divide-white/10">
                                                            {courseEntry.assignments.map((submission) => (
                                                                <tr key={submission.id}>
                                                                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900 dark:text-white">{submission.assignment_title}</td>
                                                                    <td className="whitespace-nowrap px-4 py-3">
                                                                        <span className={clsx(
                                                                            'inline-flex rounded-full px-2 text-xs font-semibold leading-5',
                                                                            submission.status === 'approved' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                                                            : submission.status === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                                                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
                                                                        )}>
                                                                            {submission.status === 'approved' ? 'Aprobado'
                                                                            : submission.status === 'rejected' ? 'Rechazado'
                                                                            : 'Pendiente'}
                                                                        </span>
                                                                    </td>
                                                                    <td className="max-w-xs truncate px-4 py-3 text-sm text-gray-600 dark:text-neutral-400">{submission.teacher_comment || '—'}</td>
                                                                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500 dark:text-neutral-400">{formatDateTime(submission.reviewed_at)}</td>
                                                                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500 dark:text-neutral-400">{submission.reviewer_name || '—'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )
                                        )}
                                    </div>
                                </GlassCard>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default Ficha;
